import { baseApi, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import { onecEnvelopeDecoder, type OneCEnvelope } from '../../../shared/api/onecEnvelope'
import { normalizeRid } from '../../../shared/model/scopeKeys'
import { KEEP_DIRECTORY_SECONDS, KEEP_PAST_SECONDS } from '../model/constants'
import type { CashBounds, CashBoundsArgs, CashCorrectionReceipts, CashCorrectionsArgs, CashReceiptArgs, CashReceiptDetails, CashReceipts, CashReceiptsArgs, CashRegisters, CashSummary, CashSummaryArgs } from '../model/types'
import { decodeCashBounds, decodeCashCorrections, decodeCashReceipt, decodeCashReceipts, decodeCashRegisters, decodeCashSummary } from './contracts'
import { ONEC_RECEIPTS_URLS } from './urls'

/**
 * Чеки базы 1С (RP-FB12) в ОБЩЕМ `baseApi`: кассы ККМ, чеки и возвраты за период, чек целиком. Служба читает 1С на
 * лету и ничего не хранит; в браузер чеки не сохраняются — живут только в кэше запроса. Пишущих ручек нет.
 */
export const ONEC_RECEIPTS_TAG = 'OneCCashReceipts' as const

export interface CashRegistersArgs {
  resourceId: string
  fresh?: number
}

export const receiptsTag = (resourceId: string) => resourceTag(ONEC_RECEIPTS_TAG, normalizeRid(resourceId), 'RECEIPTS')

export const receiptsApi = baseApi.enhanceEndpoints({ addTagTypes: [ONEC_RECEIPTS_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    onecCashRegisters: build.query<OneCEnvelope<CashRegisters>, CashRegistersArgs>({
      keepUnusedDataFor: KEEP_DIRECTORY_SECONDS,
      query: ({ resourceId, fresh }) => ({
        url: ONEC_RECEIPTS_URLS.registers(resourceId, fresh),
        noRetry: true,
        decode: onecEnvelopeDecoder(resourceId, decodeCashRegisters(resourceId)),
      }),
      providesTags: (_result, _error, { resourceId }) => [receiptsTag(resourceId), resourceScopeTag(normalizeRid(resourceId))],
    }),
    onecCashReceipts: build.query<OneCEnvelope<CashReceipts>, CashReceiptsArgs>({
      keepUnusedDataFor: KEEP_PAST_SECONDS,
      query: ({ resourceId, from, to, registerKeys, fresh }) => ({
        url: ONEC_RECEIPTS_URLS.list(resourceId, from, to, registerKeys, fresh),
        // Чтение идёт в 1С: отказ показывается сразу, следующий запрос — «Повторить» человека.
        noRetry: true,
        decode: onecEnvelopeDecoder(resourceId, decodeCashReceipts(resourceId)),
      }),
      providesTags: (_result, _error, { resourceId }) => [receiptsTag(resourceId), resourceScopeTag(normalizeRid(resourceId))],
    }),
    /** Чеки коррекции за период (RP-ST15): только список, в итоги и графики не входят. */
    onecCashCorrections: build.query<OneCEnvelope<CashCorrectionReceipts>, CashCorrectionsArgs>({
      keepUnusedDataFor: KEEP_PAST_SECONDS,
      query: ({ resourceId, from, to, fresh }) => ({
        url: ONEC_RECEIPTS_URLS.corrections(resourceId, from, to, fresh),
        noRetry: true,
        decode: onecEnvelopeDecoder(resourceId, decodeCashCorrections(resourceId)),
      }),
      providesTags: (_result, _error, { resourceId }) => [receiptsTag(resourceId), resourceScopeTag(normalizeRid(resourceId))],
    }),
    onecCashBounds: build.query<OneCEnvelope<CashBounds>, CashBoundsArgs>({
      keepUnusedDataFor: KEEP_DIRECTORY_SECONDS,
      query: ({ resourceId, fresh }) => ({
        url: ONEC_RECEIPTS_URLS.bounds(resourceId, fresh),
        noRetry: true,
        decode: onecEnvelopeDecoder(resourceId, decodeCashBounds()),
      }),
      providesTags: (_result, _error, { resourceId }) => [receiptsTag(resourceId), resourceScopeTag(normalizeRid(resourceId))],
    }),
    onecCashSummary: build.query<OneCEnvelope<CashSummary>, CashSummaryArgs>({
      keepUnusedDataFor: KEEP_PAST_SECONDS,
      query: ({ resourceId, from, to, fresh }) => ({
        url: ONEC_RECEIPTS_URLS.summary(resourceId, from, to, fresh),
        noRetry: true,
        decode: onecEnvelopeDecoder(resourceId, decodeCashSummary(resourceId)),
      }),
      // «Обновить» (`fresh`) перечитывает тот же кусок периода, а не заводит новый: прочитанное остаётся на экране до ответа.
      serializeQueryArgs: ({ queryArgs }) => { const { fresh: _fresh, ...args } = queryArgs; return args },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.fresh !== previousArg?.fresh,
      providesTags: (_result, _error, { resourceId }) => [receiptsTag(resourceId), resourceScopeTag(normalizeRid(resourceId))],
    }),
    onecCashReceipt: build.query<OneCEnvelope<CashReceiptDetails>, CashReceiptArgs>({
      query: ({ resourceId, kind, key }) => ({
        url: ONEC_RECEIPTS_URLS.receipt(resourceId, kind, key),
        noRetry: true,
        decode: onecEnvelopeDecoder(resourceId, decodeCashReceipt(resourceId)),
      }),
      providesTags: (_result, _error, { resourceId }) => [receiptsTag(resourceId), resourceScopeTag(normalizeRid(resourceId))],
    }),
  }),
})

export const { onecCashRegisters, onecCashReceipts, onecCashCorrections, onecCashSummary, onecCashBounds, onecCashReceipt } = receiptsApi.endpoints

/** «Повторить»: перечитать кассы и чеки базы. Тег — внутреннее дело пакета. */
export const reloadCashReceipts = (resourceId: string) => receiptsApi.util.invalidateTags([receiptsTag(resourceId)])
